import { Fragment, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { aiPipelineApi, type AiContent, type AiItem, type AiQuestion, type AiRun } from '@/api/aiPipeline';
import { AudioPlayer } from '@/components/ui/AudioPlayer';
import { Button } from '@/components/ui/Button';
import { Card, CardTitle } from '@/components/ui/Card';
import { useConfirm } from '@/components/ui/ConfirmProvider';
import { Input } from '@/components/ui/Input';
import { Pill } from '@/components/ui/Pill';
import { useIeltsTypeLabel } from '@/hooks/useIeltsTypeLabel';
import { useIsMobile } from '@/hooks/useIsMobile';
import {
  STAGES,
  approveBlock,
  asText,
  durationSec,
  formatDateTime,
  isBusy,
  isPublished,
  isRecord,
  pollInterval,
  prettyJson,
  publishBlock,
  rejectBlock,
  rerunBlock,
  runsOf,
  safeMediaUrl,
  splitIssues,
  verdictsByTask,
  type PlacedIssue,
  type Stage,
  type TaskVerdict,
} from '@/lib/aiPipeline';
import { cn } from '@/lib/utils';

import { ChecksPill, ItemKinds, PassPill, ReviewPill, RunPill, StatePill } from './labels';
import { ApiErrorText, NoteLine, type Note } from './notices';

/**
 * Карточка элемента конвейера (договор §3 `GET /items/:id`, §7).
 *
 * ⚠️ ВСЁ ОТ МОДЕЛИ — ТЕКСТОМ. `rendered`, `raw_output`, вердикты, тексты заданий
 * выводятся обычными узлами React, без разметки и без подстановок i18n.
 * ⚠️ ОПРОС ИДЁТ, ПОКА КОНВЕЙЕР ЗАНЯТ (`pollInterval`): раскрытые блоки держатся в
 * состоянии по ключу, поэтому обновление раз в 4 с их не сворачивает.
 */

const LABEL = 'mb-1.5 block text-xs font-semibold uppercase tracking-label text-on-surface-variant';
const FIELD =
  'app-field w-full rounded-button bg-surface-container-lowest px-4 py-3 text-sm text-on-surface shadow-soft placeholder:text-on-surface-variant/60 focus:shadow-ambient focus:outline-none';
const BODY = 'max-h-96 overflow-auto whitespace-pre-wrap break-words rounded-xl bg-surface-container px-3 py-2 text-sm';
const PRE = 'mt-1 max-h-96 overflow-auto whitespace-pre-wrap break-words rounded-xl bg-surface-container px-3 py-2 font-mono text-xs text-on-surface';

type Props = { id: string; onBack: () => void };

export function ItemCard({ id, onBack }: Props) {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const typeLabel = useIeltsTypeLabel();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const q = useQuery({
    queryKey: ['ai-pipeline', 'item', id],
    queryFn: () => aiPipelineApi.getItem(id),
    refetchInterval: (query) => pollInterval(query.state.data?.item),
    // ⚠️ Скрытая вкладка не останавливает опрос: иначе react-query ставит его на паузу,
    // и карточка стоит на «генерации», пока проверяющая на другой вкладке (стенд 11.09).
    refetchIntervalInBackground: true,
  });

  const [comment, setComment] = useState('');
  const [exclude, setExclude] = useState<string[]>([]);
  // «Одобрил» ≠ «опубликовал» (29.09.2026): по умолчанию одобрение ученикам не показывает.
  const [publishNow, setPublishNow] = useState(false);
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  const [ratingPick, setRatingPick] = useState<number | null>(null);
  const [ratingNote, setRatingNote] = useState<string | null>(null);
  const [decisionNote, setDecisionNote] = useState<Note>(null);
  const [decisionError, setDecisionError] = useState<unknown>(null);
  const [ratingMsg, setRatingMsg] = useState<Note>(null);
  const [ratingError, setRatingError] = useState<unknown>(null);
  const [stageNote, setStageNote] = useState<Note>(null);
  const [stageError, setStageError] = useState<unknown>(null);

  const refresh = () => qc.invalidateQueries({ queryKey: ['ai-pipeline'] });

  const approveM = useMutation({
    mutationFn: (body: { comment: string; exclude_ids: string[]; publish: boolean }) => aiPipelineApi.approve(id, body),
    onSuccess: (_data, body) => {
      setComment('');
      setExclude([]);
      setPublishNow(false);
      setDecisionNote({
        tone: 'ok',
        text: `${t('adminAiPipeline.decision.approved')} ${t(body.publish ? 'adminAiPipeline.decision.approvedShown' : 'adminAiPipeline.decision.approvedHidden')}`,
      });
      void refresh();
    },
    onError: (e) => setDecisionError(e),
  });
  const publishM = useMutation({
    mutationFn: (show: boolean) => (show ? aiPipelineApi.publish(id) : aiPipelineApi.unpublish(id)),
    onSuccess: (_data, show) => {
      setDecisionNote({ tone: 'ok', text: t(show ? 'adminAiPipeline.decision.published' : 'adminAiPipeline.decision.unpublished') });
      void refresh();
    },
    onError: (e) => setDecisionError(e),
  });
  const rejectM = useMutation({
    mutationFn: (v: { comment: string; wasApproved: boolean }) => aiPipelineApi.reject(id, v.comment),
    onSuccess: (_data, v) => {
      setComment('');
      setDecisionNote({ tone: 'ok', text: t(v.wasApproved ? 'adminAiPipeline.decision.unapproved' : 'adminAiPipeline.decision.rejected') });
      void refresh();
    },
    onError: (e) => setDecisionError(e),
  });
  const rateM = useMutation({
    mutationFn: (v: { rating: number; note: string }) => aiPipelineApi.rate(id, v.rating, v.note),
    onSuccess: (_data, v) => {
      setRatingPick(null);
      setRatingNote(null);
      setRatingMsg({ tone: 'ok', text: t('adminAiPipeline.rating.saved', { n: v.rating }) });
      void refresh();
    },
    onError: (e) => setRatingError(e),
  });
  const rerunM = useMutation({
    mutationFn: (stage: Stage) => aiPipelineApi.rerun(id, stage),
    onSuccess: (_data, stage) => {
      setStageNote({ tone: 'ok', text: t('adminAiPipeline.stage.rerunDone', { stage: t(`adminAiPipeline.stageName.${stage}`) }) });
      void refresh();
    },
    onError: (e) => setStageError(e),
  });

  const view = q.data;
  if (!view?.item) {
    return (
      <div data-ai-card className="space-y-3">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <span aria-hidden className="material-symbols-outlined text-lg">
            arrow_back
          </span>
          {t('adminAiPipeline.card.back')}
        </Button>
        {q.isError ? (
          <ApiErrorText error={q.error} />
        ) : (
          <Card>
            <p className="text-sm text-on-surface-variant">{t('adminAiPipeline.card.loading')}</p>
          </Card>
        )}
      </div>
    );
  }

  const it = view.item;
  const runs = view.runs ?? [];
  const c: AiContent = view.content ?? { tasks: [], writing: [], speaking: [] };
  const tasks = c.tasks ?? [];
  const writing = c.writing ?? [];
  const speaking = c.speaking ?? [];
  const contentIds = [...tasks.map((x) => x.id), ...writing.map((x) => x.id), ...speaking.map((x) => x.id)];
  const busy = isBusy(it);
  const failed = it.checks_passed === false;
  const verdicts = verdictsByTask(runs);
  const lang = i18n.language;

  const toggle = (k: string) =>
    setOpen((s) => {
      const next = new Set(s);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  const toggleExclude = (taskId: string) => setExclude((s) => (s.includes(taskId) ? s.filter((x) => x !== taskId) : [...s, taskId]));

  const onApprove = () => {
    setDecisionError(null);
    setDecisionNote(null);
    const block = approveBlock(it, { comment, excluded: exclude.length, total: contentIds.length });
    if (block === 'comment_required') {
      setDecisionNote({ tone: 'hint', text: t('adminAiPipeline.decision.commentRequiredFailed') });
      return;
    }
    if (block === 'exclude_all') {
      setDecisionNote({ tone: 'hint', text: t('adminAiPipeline.decision.excludeAll') });
      return;
    }
    if (block) return;
    approveM.mutate({ comment: comment.trim(), exclude_ids: exclude, publish: publishNow });
  };

  // Публикация и снятие — с подтверждением: это то, что увидят ученики.
  const onPublish = async (show: boolean) => {
    setDecisionError(null);
    setDecisionNote(null);
    if (publishBlock(it)) return;
    const ok = await confirm({
      title: t(show ? 'adminAiPipeline.decision.publishTitle' : 'adminAiPipeline.decision.unpublishTitle'),
      message: t(show ? 'adminAiPipeline.decision.publishMessage' : 'adminAiPipeline.decision.unpublishMessage'),
      confirmLabel: t(show ? 'adminAiPipeline.decision.publish' : 'adminAiPipeline.decision.unpublish'),
      danger: !show,
    });
    if (!ok) return;
    publishM.mutate(show);
  };

  const onReject = () => {
    setDecisionError(null);
    setDecisionNote(null);
    const block = rejectBlock(it, comment);
    if (block === 'comment_required') {
      setDecisionNote({ tone: 'hint', text: t('adminAiPipeline.decision.commentRequiredReject') });
      return;
    }
    if (block) return;
    rejectM.mutate({ comment: comment.trim(), wasApproved: it.review_status === 'approved' });
  };

  const pick = ratingPick ?? it.rating ?? 0;
  const noteValue = ratingNote ?? it.rating_note ?? '';
  const onRate = () => {
    setRatingError(null);
    setRatingMsg(null);
    if (!pick) {
      setRatingMsg({ tone: 'hint', text: t('adminAiPipeline.rating.pick') });
      return;
    }
    rateM.mutate({ rating: pick, note: noteValue.trim() });
  };

  const onRerun = async (stage: Stage) => {
    setStageError(null);
    setStageNote(null);
    if (stage === 'generate') {
      const ok = await confirm({
        title: t('adminAiPipeline.stage.rerunGenerateTitle'),
        message: t('adminAiPipeline.stage.rerunGenerateMessage'),
        confirmLabel: t('adminAiPipeline.stage.rerunGenerateConfirm'),
        danger: true,
      });
      if (!ok) return;
    }
    rerunM.mutate(stage);
  };

  // ── Сводка ──
  const ratingText = it.rating
    ? [t('adminAiPipeline.card.ratingValue', { n: it.rating }), it.rating_note].filter(Boolean).join(' — ')
    : t('adminAiPipeline.card.ratingNone');
  const summary: { label: string; value: ReactNode }[] = [
    {
      label: t('adminAiPipeline.card.skill'),
      value: (
        <>
          {t(`adminAiPipeline.skill.${it.skill}`, it.skill)}
          {' · '}
          <ItemKinds item={it} />
        </>
      ),
    },
    { label: t('adminAiPipeline.card.level'), value: it.cefr_level || '—' },
    { label: t('adminAiPipeline.card.pipeline'), value: <StatePill state={it.pipeline_state} /> },
    { label: t('adminAiPipeline.card.checks'), value: <ChecksPill passed={it.checks_passed} /> },
    { label: t('adminAiPipeline.card.review'), value: <ReviewPill status={it.review_status} /> },
  ];
  if (it.listening_part) {
    summary.splice(1, 0, { label: t('adminAiPipeline.card.part'), value: t(`adminAiPipeline.generate.partName.${it.listening_part}`) });
  }
  if (it.review_status === 'approved') {
    summary.push({
      label: t('adminAiPipeline.card.published'),
      value: isPublished(it) ? (
        <Pill variant="success">{t('adminAiPipeline.card.publishedAt', { at: formatDateTime(it.published_at, lang) })}</Pill>
      ) : (
        <Pill>{t('adminAiPipeline.card.notPublished')}</Pill>
      ),
    });
  }
  if (it.review_comment) summary.push({ label: t('adminAiPipeline.card.reviewComment'), value: it.review_comment });
  if (it.reviewed_at) {
    summary.push({
      label: t('adminAiPipeline.card.reviewed'),
      value: [formatDateTime(it.reviewed_at, lang), it.reviewed_by].filter(Boolean).join(' · ') || '—',
    });
  }
  summary.push(
    { label: t('adminAiPipeline.card.rating'), value: ratingText },
    { label: t('adminAiPipeline.card.trigger'), value: t(`adminAiPipeline.trigger.${it.trigger}`, it.trigger || '—') },
    { label: t('adminAiPipeline.card.created'), value: formatDateTime(it.created_at, lang) || '—' },
    { label: t('adminAiPipeline.card.updated'), value: formatDateTime(it.updated_at, lang) || '—' },
  );

  // ── Решение ──
  const hints: string[] = [];
  if (it.review_status !== 'pending') {
    hints.push(t('adminAiPipeline.decision.notPending', { status: t(`adminAiPipeline.review.${it.review_status}`, it.review_status) }));
  }
  if (it.review_status === 'pending' && it.pipeline_state === 'error') hints.push(t('adminAiPipeline.decision.stageError'));
  if (failed) hints.push(t('adminAiPipeline.decision.failedHint'));
  if (it.review_status === 'approved') {
    hints.push(t(isPublished(it) ? 'adminAiPipeline.decision.approvedShown' : 'adminAiPipeline.decision.approvedHidden'));
  }
  if (contentIds.length) hints.push(t('adminAiPipeline.decision.excludeHint'));

  // ── Содержимое: task_index сквозной по tasks → writing → speaking ──
  const blocks: ReactNode[] = [];
  let index = 0;
  for (const task of tasks) {
    const i = index++;
    const qs = task.questions ?? [];
    const split = splitIssues(verdicts[i], qs.map((x) => Number(x.ord)));
    blocks.push(
      <TaskBox
        key={task.id}
        id={task.id}
        index={i}
        title={
          task.segment_ord
            ? t('adminAiPipeline.content.half', { n: task.segment_ord, title: `${typeLabel(task.type)} (${task.type})` })
            : `${typeLabel(task.type)} (${task.type})`
        }
        reviewStatus={task.review_status}
        verdict={verdicts[i]}
        excluded={exclude.includes(task.id)}
        onExclude={() => toggleExclude(task.id)}
        issues={split.general}
      >
        {task.instruction ? <p className="whitespace-pre-wrap text-sm">{task.instruction}</p> : null}
        {task.group_label ? <LabeledText label={t('adminAiPipeline.content.group')} text={task.group_label} /> : null}
        {task.options?.length ? <LabeledText label={t('adminAiPipeline.content.taskOptions')} text={task.options.join(' · ')} /> : null}
        {qs.length ? (
          <ol className="space-y-2">
            {qs.map((x) => (
              <QuestionRow key={x.id || x.ord} q={x} issues={split.byOrd[Number(x.ord)] ?? []} />
            ))}
          </ol>
        ) : null}
      </TaskBox>,
    );
  }
  for (const w of writing) {
    const i = index++;
    blocks.push(
      <TaskBox
        key={w.id}
        id={w.id}
        index={i}
        title={t('adminAiPipeline.content.writing', {
          kind: t(`adminAiPipeline.kind.${w.kind}`, w.kind),
          subtype: t(`adminAiPipeline.subtype.${w.subtype}`, w.subtype),
        })}
        reviewStatus={w.review_status}
        verdict={verdicts[i]}
        excluded={exclude.includes(w.id)}
        onExclude={() => toggleExclude(w.id)}
        issues={splitIssues(verdicts[i], []).general}
      >
        <div className={BODY}>{w.prompt}</div>
      </TaskBox>,
    );
  }
  for (const s of speaking) {
    const i = index++;
    blocks.push(
      <TaskBox
        key={s.id}
        id={s.id}
        index={i}
        title={t('adminAiPipeline.content.speaking', { part: s.part })}
        reviewStatus={s.review_status}
        verdict={verdicts[i]}
        excluded={exclude.includes(s.id)}
        onExclude={() => toggleExclude(s.id)}
        issues={splitIssues(verdicts[i], []).general}
      >
        <LabeledText label={t('adminAiPipeline.content.topic')} text={s.topic || '—'} />
        {s.cue_text ? <div className={BODY}>{s.cue_text}</div> : null}
        {s.questions?.length ? (
          <ol className="list-decimal space-y-1 pl-5 text-sm">
            {s.questions.map((qq, k) => (
              <li key={k} className="whitespace-pre-wrap">
                {qq}
              </li>
            ))}
          </ol>
        ) : null}
      </TaskBox>,
    );
  }
  const audioSrc = safeMediaUrl(c.audio?.audio_url);

  return (
    <div data-ai-card className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <span aria-hidden className="material-symbols-outlined text-lg">
            arrow_back
          </span>
          {t('adminAiPipeline.card.back')}
        </Button>
        <Button variant="secondary" size="sm" onClick={() => void q.refetch()}>
          <span aria-hidden className="material-symbols-outlined text-lg">
            refresh
          </span>
          {t('adminAiPipeline.common.refresh')}
        </Button>
      </div>
      {busy ? (
        <div className="rounded-xl bg-primary-fixed px-3 py-2 text-sm text-on-primary-fixed-variant">{t('adminAiPipeline.card.busy')}</div>
      ) : null}
      {it.last_error ? (
        <div className="whitespace-pre-wrap rounded-xl bg-error-container px-3 py-2 text-sm text-on-error-container">
          {`${t('adminAiPipeline.card.lastError')} ${it.last_error}`}
        </div>
      ) : null}

      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,380px)]">
        <div className="min-w-0 space-y-4 lg:order-2">
          <Card className="space-y-3">
            <CardTitle>{t('adminAiPipeline.card.title')}</CardTitle>
            <div className="break-all font-mono text-xs text-on-surface-variant">{it.id}</div>
            <dl className="grid grid-cols-[max-content_minmax(0,1fr)] items-center gap-x-4 gap-y-2 text-sm">
              {summary.map((row) => (
                <Fragment key={row.label}>
                  <dt className="text-on-surface-variant">{row.label}</dt>
                  <dd className="min-w-0 break-words">{row.value}</dd>
                </Fragment>
              ))}
            </dl>
          </Card>

          <Card data-ai-decision className="space-y-3">
            <CardTitle>{t('adminAiPipeline.decision.title')}</CardTitle>
            {hints.map((h) => (
              <p key={h} className="text-sm text-on-surface-variant">
                {h}
              </p>
            ))}
            <label className="block">
              <span className={LABEL}>{t('adminAiPipeline.decision.comment')}</span>
              <textarea
                data-ai-comment
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={failed ? t('adminAiPipeline.decision.commentPlaceholderFailed') : t('adminAiPipeline.decision.commentPlaceholder')}
                className={FIELD}
              />
            </label>
            {it.review_status === 'pending' ? (
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  data-ai-publish-now
                  checked={publishNow}
                  onChange={(e) => setPublishNow(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-primary"
                />
                <span>
                  <span className="font-medium">{t('adminAiPipeline.decision.publishNow')}</span>
                  <span className="block text-xs text-on-surface-variant">{t('adminAiPipeline.decision.publishNowHint')}</span>
                </span>
              </label>
            ) : null}
            {/* Телефон (§5.16): «Одобрить / Отклонить» прибиты к низу над нижним меню, пока раздел «Решение» на экране, —
                комментарий и галочки исключения выше, и кнопки иначе уезжали под клавиатуру и меню. */}
            <div
              data-ai-decision-bar
              className={cn(
                'flex flex-wrap gap-2',
                isMobile && 'sticky z-10 -mx-6 border-t border-outline bg-surface-container-lowest px-6 py-3',
              )}
              style={isMobile ? { bottom: 'calc(4.25rem + var(--sa-bottom))' } : undefined}
            >
              {it.review_status === 'approved' ? (
                <Button
                  data-ai-publish={isPublished(it) ? 'off' : 'on'}
                  variant={isPublished(it) ? 'secondary' : 'primary'}
                  disabled={busy}
                  loading={publishM.isPending}
                  onClick={() => void onPublish(!isPublished(it))}
                >
                  {isPublished(it) ? t('adminAiPipeline.decision.unpublish') : t('adminAiPipeline.decision.publish')}
                </Button>
              ) : null}
              <Button
                data-ai-approve
                disabled={busy || it.review_status !== 'pending' || it.pipeline_state !== 'done'}
                loading={approveM.isPending}
                onClick={onApprove}
              >
                {exclude.length ? t('adminAiPipeline.decision.approveExcluded', { n: exclude.length }) : t('adminAiPipeline.decision.approve')}
              </Button>
              <Button data-ai-reject variant="danger" disabled={busy || it.review_status === 'rejected'} loading={rejectM.isPending} onClick={onReject}>
                {it.review_status === 'approved' ? t('adminAiPipeline.decision.unapprove') : t('adminAiPipeline.decision.reject')}
              </Button>
            </div>
            <NoteLine note={decisionNote} />
            {decisionError ? <ApiErrorText error={decisionError} /> : null}
          </Card>

          <Card className="space-y-3">
            <CardTitle>{t('adminAiPipeline.rating.title')}</CardTitle>
            <p className="text-sm text-on-surface-variant">{t('adminAiPipeline.rating.hint')}</p>
            <div className="flex flex-wrap gap-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  data-ai-star={n}
                  aria-pressed={n === pick}
                  aria-label={t('adminAiPipeline.rating.star', { n })}
                  onClick={() => {
                    setRatingPick(n);
                    setRatingMsg(null);
                  }}
                  className={cn(
                    'flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold transition',
                    n <= pick ? 'bg-primary text-on-primary' : 'bg-surface-container text-on-surface-variant',
                  )}
                >
                  {n}
                </button>
              ))}
            </div>
            <Input
              data-ai-rating-note
              label={t('adminAiPipeline.rating.note')}
              placeholder={t('adminAiPipeline.rating.notePlaceholder')}
              value={noteValue}
              onChange={(e) => setRatingNote(e.target.value)}
            />
            <Button data-ai-rating-save variant="secondary" size="sm" loading={rateM.isPending} onClick={onRate}>
              {t('adminAiPipeline.rating.save')}
            </Button>
            <NoteLine note={ratingMsg} />
            {ratingError ? <ApiErrorText error={ratingError} /> : null}
          </Card>
        </div>

        <div className="min-w-0 space-y-4 lg:order-1">
          <Card className="space-y-4">
            <CardTitle>{t('adminAiPipeline.content.title')}</CardTitle>
            {c.text ? (
              <div className="space-y-1">
                <LabeledText label={t('adminAiPipeline.content.text')} text={c.text.title || t('adminAiPipeline.content.untitled')} strong />
                <div className="text-xs text-on-surface-variant">{t('adminAiPipeline.content.words', { n: c.text.word_count ?? 0 })}</div>
                <div className={BODY}>{c.text.body}</div>
              </div>
            ) : null}
            {c.audio ? (
              <div className="space-y-2">
                <LabeledText label={t('adminAiPipeline.content.audio')} text={c.audio.title || t('adminAiPipeline.content.untitled')} strong />
                {audioSrc ? (
                  <AudioPlayer src={audioSrc} preload="none" />
                ) : (
                  <p className="text-sm text-on-surface-variant">{t('adminAiPipeline.content.noAudio')}</p>
                )}
                <Toggle k="transcript" label={t('adminAiPipeline.content.transcript')} open={open.has('transcript')} onToggle={toggle} />
                {open.has('transcript') ? <div className={BODY}>{c.audio.transcript || t('adminAiPipeline.stage.empty')}</div> : null}
              </div>
            ) : null}
            {blocks}
            {!blocks.length && !c.text && !c.audio ? (
              <p className="text-sm text-on-surface-variant">
                {busy ? t('adminAiPipeline.content.emptyBusy') : t('adminAiPipeline.content.empty')}
              </p>
            ) : null}
          </Card>

          <Card className="space-y-3">
            <CardTitle>{t('adminAiPipeline.stage.title')}</CardTitle>
            <p className="text-sm text-on-surface-variant">{t('adminAiPipeline.stage.hint')}</p>
            <NoteLine note={stageNote} />
            {stageError ? <ApiErrorText error={stageError} /> : null}
            {STAGES.map((stage) => (
              <StageBox
                key={stage}
                stage={stage}
                runs={runs}
                item={it}
                open={open}
                onToggle={toggle}
                onRerun={(s) => void onRerun(s)}
                pending={rerunM.isPending}
              />
            ))}
          </Card>
        </div>
      </div>
    </div>
  );
}

function LabeledText({ label, text, strong }: { label: string; text: string; strong?: boolean }) {
  return (
    <div className={cn('whitespace-pre-wrap text-sm', strong && 'font-semibold')}>
      <span className={strong ? undefined : 'text-on-surface-variant'}>{label}</span> {text}
    </div>
  );
}

function Toggle({ k, label, open, onToggle }: { k: string; label: string; open: boolean; onToggle: (k: string) => void }) {
  return (
    <button
      type="button"
      data-ai-toggle={k}
      aria-expanded={open}
      onClick={() => onToggle(k)}
      className="flex items-center gap-1 text-left text-sm font-medium text-primary"
    >
      <span aria-hidden className="material-symbols-outlined text-lg">
        {open ? 'expand_less' : 'expand_more'}
      </span>
      {label}
    </button>
  );
}

function IssueNote({ issue }: { issue: PlacedIssue }) {
  const { t } = useTranslation();
  const ord = Number(issue.question_ord);
  const head = [
    t(issue.stage === 'quality' ? 'adminAiPipeline.content.issueQuality' : 'adminAiPipeline.content.issueFormat'),
    ord > 0 ? t('adminAiPipeline.content.issueQuestion', { n: ord }) : '',
  ]
    .filter(Boolean)
    .join(', ');
  const suggestion = asText(issue.suggestion);
  return (
    <div data-ai-issue={issue.stage} className="rounded-lg border-l-4 border-warning bg-warning-container px-2 py-1 text-xs text-on-warning-container">
      <span className="font-semibold">{`${head}: `}</span>
      <span className="whitespace-pre-wrap">{asText(issue.problem)}</span>
      {suggestion ? <div className="mt-0.5 whitespace-pre-wrap opacity-80">{`${t('adminAiPipeline.content.advice')} ${suggestion}`}</div> : null}
    </div>
  );
}

function QuestionRow({ q, issues }: { q: AiQuestion; issues: PlacedIssue[] }) {
  const { t } = useTranslation();
  return (
    <li data-ai-question={q.ord} className="space-y-1 rounded-xl bg-surface-container-lowest px-3 py-2 text-sm">
      <div className="whitespace-pre-wrap">
        <span className="font-semibold">{`${q.ord}. `}</span>
        {q.prompt}
      </div>
      {q.options?.length ? (
        <div className="text-xs text-on-surface-variant">{`${t('adminAiPipeline.content.options')} ${q.options.join(' · ')}`}</div>
      ) : null}
      <div className="flex flex-wrap items-baseline gap-x-1.5">
        <span className="text-on-surface-variant">{t('adminAiPipeline.content.key')}</span>
        <span className="font-semibold text-primary">{q.answer_key || '—'}</span>
        {q.synonyms?.length ? (
          <span className="text-xs text-on-surface-variant">{`${t('adminAiPipeline.content.synonyms')} ${q.synonyms.join(', ')}`}</span>
        ) : null}
      </div>
      {q.needs_review ? (
        <div className="rounded-lg bg-warning-container px-2 py-1 text-xs text-on-warning-container">
          {[t('adminAiPipeline.content.needsReview'), q.review_note].filter(Boolean).join(': ')}
        </div>
      ) : null}
      {issues.map((iss, k) => (
        <IssueNote key={k} issue={iss} />
      ))}
    </li>
  );
}

type TaskBoxProps = {
  id: string;
  index: number;
  title: string;
  reviewStatus: string;
  verdict: TaskVerdict | undefined;
  excluded: boolean;
  onExclude: () => void;
  issues: PlacedIssue[];
  children?: ReactNode;
};

function TaskBox({ id, index, title, reviewStatus, verdict, excluded, onExclude, issues, children }: TaskBoxProps) {
  const { t } = useTranslation();
  return (
    <div data-ai-task={id} className={cn('space-y-2 rounded-2xl bg-surface-container-low p-4', excluded && 'opacity-60')}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          <span className="font-semibold">{t('adminAiPipeline.content.taskTitle', { n: index + 1, title })}</span>
          <ReviewPill status={reviewStatus} />
          {verdict?.quality ? <PassPill label={t('adminAiPipeline.content.quality')} pass={verdict.quality.pass} /> : null}
          {verdict?.format ? <PassPill label={t('adminAiPipeline.content.format')} pass={verdict.format.pass} /> : null}
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input type="checkbox" data-ai-exclude={id} checked={excluded} onChange={onExclude} className="h-4 w-4 accent-primary" />
          {t('adminAiPipeline.content.exclude')}
        </label>
      </div>
      <div className="break-all font-mono text-xs text-on-surface-variant">{id}</div>
      {issues.length ? (
        <div data-ai-task-issues className="space-y-1">
          {issues.map((iss, k) => (
            <IssueNote key={k} issue={iss} />
          ))}
        </div>
      ) : null}
      {children}
    </div>
  );
}

type StageBoxProps = {
  stage: Stage;
  runs: AiRun[];
  item: AiItem;
  open: Set<string>;
  onToggle: (k: string) => void;
  onRerun: (stage: Stage) => void;
  pending: boolean;
};

function StageBox({ stage, runs, item, open, onToggle, onRerun, pending }: StageBoxProps) {
  const { t } = useTranslation();
  const rs = runsOf(runs, stage);
  const latest = rs.length ? rs[rs.length - 1] : null;
  const block = rerunBlock(item, stage);
  const historyKey = `history:${stage}`;
  return (
    <div data-ai-stage={stage} className="space-y-2 rounded-2xl bg-surface-container-low p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="font-semibold">{t(`adminAiPipeline.stageName.${stage}`)}</span>
          {latest ? <RunPill status={latest.status} /> : <Pill>{t('adminAiPipeline.run.never')}</Pill>}
          {latest ? <span className="text-xs text-on-surface-variant">{t('adminAiPipeline.common.attempt', { n: latest.attempt })}</span> : null}
        </div>
        <Button data-ai-rerun={stage} variant="secondary" size="sm" disabled={!!block || pending} onClick={() => onRerun(stage)}>
          <span aria-hidden className="material-symbols-outlined text-base">
            replay
          </span>
          {t('adminAiPipeline.stage.rerun')}
        </Button>
      </div>
      {block === 'approved_generate' ? <p className="text-xs text-on-surface-variant">{t('adminAiPipeline.stage.rerunLocked')}</p> : null}
      {latest ? <RunBody run={latest} stage={stage} open={open} onToggle={onToggle} /> : null}
      {rs.length > 1 ? (
        <>
          <Toggle k={historyKey} label={t('adminAiPipeline.stage.history', { n: rs.length - 1 })} open={open.has(historyKey)} onToggle={onToggle} />
          {open.has(historyKey)
            ? rs
                .slice(0, -1)
                .reverse()
                .map((r) => (
                  <div key={r.id || `${stage}:${r.attempt}`} className="space-y-1 rounded-xl bg-surface-container-lowest p-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">{t('adminAiPipeline.stage.attemptN', { n: r.attempt })}</span>
                      <RunPill status={r.status} />
                    </div>
                    <RunBody run={r} stage={stage} open={open} onToggle={onToggle} />
                  </div>
                ))
            : null}
        </>
      ) : null}
    </div>
  );
}

function RunBody({ run, stage, open, onToggle }: { run: AiRun; stage: Stage; open: Set<string>; onToggle: (k: string) => void }) {
  const { t, i18n } = useTranslation();
  const base = run.id || `${stage}:${run.attempt}`;
  const sec = durationSec(run.started_at, run.finished_at);
  const started = formatDateTime(run.started_at, i18n.language);
  const took =
    sec == null
      ? ''
      : sec < 60
        ? t('adminAiPipeline.stage.seconds', { n: sec })
        : t('adminAiPipeline.stage.minutes', { m: Math.floor(sec / 60), s: sec % 60 });
  const meta = [
    run.model ? t('adminAiPipeline.stage.model', { model: run.model }) : '',
    started ? t('adminAiPipeline.stage.started', { at: started }) : '',
    !run.finished_at ? t('adminAiPipeline.stage.running') : took ? t('adminAiPipeline.stage.took', { d: took }) : '',
  ]
    .filter(Boolean)
    .join(' · ');
  const fields = [
    { f: 'rendered', label: t('adminAiPipeline.stage.rendered'), text: run.rendered },
    { f: 'raw', label: t('adminAiPipeline.stage.raw'), text: run.raw_output },
    { f: 'verdict', label: t('adminAiPipeline.stage.verdict'), text: prettyJson(run.verdict) },
    ...(run.spec ? [{ f: 'spec', label: t('adminAiPipeline.stage.spec'), text: run.spec }] : []),
    { f: 'prompts', label: t('adminAiPipeline.stage.prompts'), text: prettyJson(run.prompts) },
  ];
  return (
    <div className="space-y-2">
      {meta ? <div className="text-xs text-on-surface-variant">{meta}</div> : null}
      {run.error ? (
        <div className="whitespace-pre-wrap rounded-xl bg-error-container px-3 py-2 text-sm text-on-error-container">{run.error}</div>
      ) : null}
      {stage === 'generate' ? <GenVerdict verdict={run.verdict} /> : <CheckVerdict verdict={run.verdict} />}
      <div className="space-y-1">
        {fields.map(({ f, label, text }) => {
          const k = `${base}:${f}`;
          return (
            <div key={f}>
              <Toggle k={k} label={label} open={open.has(k)} onToggle={onToggle} />
              {open.has(k) ? <pre className={PRE}>{text || t('adminAiPipeline.stage.empty')}</pre> : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function countText(x: unknown, pieces: (n: number) => string): string {
  if (Array.isArray(x)) return x.every((e) => typeof e !== 'object') ? x.map(asText).join(', ') || '—' : pieces(x.length);
  return asText(x);
}

/** Итог детерминированной проверки генерации: что принято, что отброшено и почему. */
function GenVerdict({ verdict }: { verdict: Record<string, unknown> | null }) {
  const { t } = useTranslation();
  const typeLabel = useIeltsTypeLabel();
  if (!isRecord(verdict) || !Object.keys(verdict).length) return null;
  const pieces = (n: number) => t('adminAiPipeline.stage.pieces', { n });
  const labels: [string, string][] = [
    ['accepted', t('adminAiPipeline.stage.accepted')],
    ['tasks', t('adminAiPipeline.stage.tasks')],
    ['writing', t('adminAiPipeline.stage.writing')],
    ['speaking', t('adminAiPipeline.stage.speaking')],
  ];
  const counts = labels.filter(([k]) => verdict[k] != null).map(([k, l]) => `${l}: ${countText(verdict[k], pieces)}`);
  const rejected = Array.isArray(verdict.rejected) ? verdict.rejected.filter(isRecord) : [];
  const warnings = Array.isArray(verdict.warnings) ? verdict.warnings : [];
  return (
    <div className="space-y-1 text-sm">
      {counts.length ? <div>{`${t('adminAiPipeline.stage.genSummary')} ${counts.join(' · ')}`}</div> : null}
      {rejected.length ? (
        <div>
          <div className="font-semibold">{t('adminAiPipeline.stage.rejectedList')}</div>
          <ul className="list-disc space-y-0.5 pl-5">
            {rejected.map((r, k) => (
              <li key={k} className="whitespace-pre-wrap">
                {`${typeLabel(asText(r.type) || '?')} — ${Array.isArray(r.problems) ? r.problems.map(asText).join('; ') : asText(r.problems)}`}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {warnings.length ? (
        <div>
          <div className="font-semibold">{t('adminAiPipeline.stage.warnings')}</div>
          <ul className="list-disc space-y-0.5 pl-5">
            {warnings.map((w, k) => (
              <li key={k} className="whitespace-pre-wrap">
                {asText(w)}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

/** Вердикт проверки ИИ: ответ модели, засчитанный итог, пометка службы, итог по заданиям. */
function CheckVerdict({ verdict }: { verdict: Record<string, unknown> | null }) {
  const { t } = useTranslation();
  if (!isRecord(verdict) || !Object.keys(verdict).length) return null;
  const pass = typeof verdict.pass === 'boolean' ? verdict.pass : null;
  const effective = typeof verdict._effective_pass === 'boolean' ? verdict._effective_pass : undefined;
  const tasks = Array.isArray(verdict.tasks) ? verdict.tasks.filter(isRecord) : [];
  const summary = asText(verdict.summary);
  const note = asText(verdict._note);
  return (
    <div className="space-y-1 text-sm">
      <div className="flex flex-wrap gap-1.5">
        <PassPill label={t('adminAiPipeline.stage.modelAnswer')} pass={pass} />
        {effective !== undefined ? <PassPill label={t('adminAiPipeline.stage.counted')} pass={effective} /> : null}
      </div>
      {summary ? <p className="whitespace-pre-wrap">{summary}</p> : null}
      {note ? <div className="rounded-xl bg-surface-container px-3 py-2 text-xs">{`${t('adminAiPipeline.stage.note')} ${note}`}</div> : null}
      {tasks.length ? (
        <ul className="space-y-1">
          {tasks.map((tk, k) => {
            const n = Number(tk.task_index);
            const issues = Array.isArray(tk.issues) ? tk.issues.length : 0;
            return (
              <li key={k} className="flex flex-wrap items-center gap-1.5 text-xs">
                <span>{t('adminAiPipeline.stage.taskVerdict', { n: Number.isInteger(n) ? n + 1 : '?' })}</span>
                <PassPill label={t('adminAiPipeline.stage.result')} pass={typeof tk.pass === 'boolean' ? tk.pass : null} />
                {issues ? <span className="text-on-surface-variant">{t('adminAiPipeline.stage.issuesCount', { n: issues })}</span> : null}
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
